import { createFileRoute } from "@tanstack/react-router";
import {
  Store, CheckCircle, ShieldCheck, Landmark, Search, Package, TrendingUp,
  Star, BarChart3, Boxes, Award,
} from "lucide-react";
import { PageShell, PageHero } from "@/components/site/page-shell";
import { CTABanner } from "@/components/sections/cta-banner";
import { ServiceCategoryGrid, ServiceHighlights } from "@/components/sections/service-category";

export const Route = createFileRoute("/marketing/amazon")({
  component: AmazonPage,
  head: () => ({
    meta: [
      { title: "Amazon Services — Seller Account, SEO & PPC | CoderLity" },
      { name: "description", content: "Full-service Amazon agency: seller account setup, approval, listing optimization, SEO, PPC, FBA, brand registry and reinstatement." },
      { property: "og:title", content: "Amazon Services — CoderLity" },
      { property: "og:description", content: "Launch, rank and scale on Amazon." },
      { property: "og:url", content: "/marketing/amazon" },
    ],
    links: [{ rel: "canonical", href: "https://coderlity.com/marketing/amazon" }],
  }),
});

function AmazonPage() {
  return (
    <PageShell>
      <PageHero
        eyebrow="Amazon Services"
        title="Launch, rank and scale on Amazon"
        sub="End-to-end Amazon support — from account approval to PPC scaling — handled by certified specialists."
      />
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <ServiceCategoryGrid
            groups={[
              {
                title: "Account & Compliance",
                items: [
                  { label: "Amazon Seller Account", icon: Store, desc: "Professional seller central account creation worldwide.", href: "/marketing/amazon/amazon-seller-account" },
                  { label: "Amazon Approval", icon: CheckCircle, desc: "Category, brand and gating approvals.", href: "/marketing/amazon/amazon-approval" },
                  { label: "Amazon Verification", icon: ShieldCheck, desc: "Identity, address and video verification support.", href: "/marketing/amazon/amazon-verification" },
                  { label: "Account Reinstatement", icon: Landmark, desc: "Plan of action for suspended accounts and ASINs.", href: "/marketing/amazon/account-reinstatement" },
                ],
              },
              {
                title: "Product & SEO",
                items: [
                  { label: "Amazon SEO", icon: Search, desc: "Title, bullets, backend keywords and A+ content optimization.", href: "/marketing/amazon/amazon-seo" },
                  { label: "Product Listing", icon: Package, desc: "Conversion-focused listings with photography and copy.", href: "/marketing/amazon/product-listing" },
                  { label: "Keyword Research", icon: Search, desc: "Helium 10 + Jungle Scout deep keyword discovery.", href: "/marketing/amazon/keyword-research" },
                  { label: "Keyword Ranking", icon: TrendingUp, desc: "Strategic ranking to page-1 for high-intent terms.", href: "/marketing/amazon/keyword-ranking" },
                  { label: "Product Reviews", icon: Star, desc: "TOS-compliant review generation programs.", href: "/marketing/amazon/product-reviews" },
                ],
              },
              {
                title: "Ads, FBA & Brand",
                items: [
                  { label: "Amazon PPC", icon: BarChart3, desc: "Sponsored Products, Brands and Display campaign management.", href: "/marketing/amazon/amazon-ppc" },
                  { label: "FBA Setup", icon: Boxes, desc: "Shipment plans, labels and FBA prep partner coordination.", href: "/marketing/amazon/fba-setup" },
                  { label: "Brand Registry", icon: Award, desc: "Trademark + Brand Registry enrollment and protection.", href: "/marketing/amazon/brand-registry" },
                ],
              },

            ]}
          />
        </div>
      </section>
      <section className="pb-16">
        <div className="mx-auto max-w-5xl px-4 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <h2 className="text-xl font-bold tracking-tight">Why brands choose us</h2>
            <div className="mt-4">
              <ServiceHighlights items={[
                "Certified Amazon Ads specialists",
                "TACoS-focused PPC management",
                "A+ content & brand storefronts",
                "Inventory & reorder forecasting",
                "Listing hijacker monitoring",
                "Global marketplace expansion",
              ]} />
            </div>
          </div>
        </div>
      </section>
      <CTABanner />
    </PageShell>
  );
}
